<script setup>
import { format } from 'date-fns';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Props
const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  showEventType: {
    type: Boolean,
    default: true,
  },
  showDetails: {
    type: Boolean,
    default: false,
  },
  showIcon: {
    type: Boolean,
    default: true,
  },
  showTicketName: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

// SLA Event helper methods
const getSlaEventTypeText = eventType => {
  const eventMap = {
    frt: 'Tempo de Primeira Resposta',
    nrt: 'Tempo de Próxima Resposta',
    rt: 'Tempo de Resolução',
    updated: 'SLA atualizado',
    created: 'SLA criado',
    deleted: 'SLA deletado',
  };
  return eventMap[eventType] || eventType;
};

const formatDate = dateString => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  // Check if date is valid
  if (Number.isNaN(date.getTime())) return 'N/A';
  return format(date, 'HH:mm:ss, dd/MM/yyyy');
};

const getDetails = event => {

  if (event.event_type === 'rt') {
    return {
      deadline: event.deadline_at,
      finished: event.meta?.resolved_at,
    };
  }
  if (event.event_type === 'nrt') {
    return {
      deadline: event.deadline_at,
      finished: event.meta?.next_response_at,
    };
  }
  if (event.event_type === 'frt') {
    return {
      deadline: event.deadline_at,
      finished: event.meta?.first_response_at,
    };
  }
  if (event.event_type === 'created') {
    return {
      deadline: null,
      finished: null,
    };
  }
  if (event.event_type === 'updated') {
    return {
      deadline: null,
      finished: null,
    };
  }
  if (event.event_type === 'deleted') {
    return {
      deadline: null,
      finished: null,
    };
  }
};

const getEventDetails = event => {
  return getDetails(event);
};

const getEventClass = event => {
  const details = getDetails(event);
  // Check if both dates exist and are valid before comparing
  if (event.event_type === 'created' || event.event_type === 'updated' || event.event_type === 'deleted') {
    return {
      class: 'bg-gray-100 text-gray-800',
      text: 'N/A',
    };
  }
  if (!details.deadline || !details.finished) {
    return {
      class: 'bg-gray-100 text-gray-800',
      text: t('TICKETS.SLA.PENDING'),
    };
  }

  const deadlineDate = new Date(details.deadline);
  const finishedDate = new Date(details.finished);

  // Check if dates are valid
  if (
    Number.isNaN(deadlineDate.getTime()) ||
    Number.isNaN(finishedDate.getTime())
  ) {
    return {
      class: 'bg-gray-100 text-gray-800',
      text: t('TICKETS.SLA.PENDING'),
    };
  }

  if (finishedDate <= deadlineDate) {
    return {
      class: 'bg-green-100 text-green-800',
      text: t('TICKETS.SLA.HIT'),
    };
  }
  return {
    class: 'bg-red-100 text-red-800',
    text: t('TICKETS.SLA.MISSED'),
  };
};

const formatTimeThreshold = seconds => {
  if (seconds === null || seconds === undefined) return 'N/A';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (hours >= 1) parts.push(`${hours}h`);
  if (minutes >= 1) parts.push(`${minutes}min`);

  return parts.length > 0 ? parts.join(' ') : `${Math.floor(seconds)}s`;
};

const formatFieldName = field => {
  const fieldMap = {
    name: t('TICKETS.NAME'),
    description: t('TICKETS.DESCRIPTION'),
    first_response_time_threshold: t('TICKETS.SLA.FIRST_RESPONSE'),
    next_response_time_threshold: t('TICKETS.SLA.NEXT_RESPONSE'),
    resolution_time_threshold: t('TICKETS.SLA.RESOLUTION'),
    only_during_business_hours: t('TICKETS.SLA.BUSINESS_HOURS'),
  };
  return fieldMap[field] || field;
};

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined) return 'N/A';

  if (field.includes('threshold')) {
    return formatTimeThreshold(value);
  }
  if (field === 'only_during_business_hours') {
    return value ? t('TICKETS.SLA.YES') : t('TICKETS.SLA.NO');
  }
  return String(value);
};

const getUpdatedEventTooltip = event => {
  if (event.event_type !== 'updated') {
    return null;
  }

  const meta = event.meta || {};
  const changes = meta.changes || {};

  if (Object.keys(changes).length === 0) {
    return t('TICKETS.SLA.NO_CHANGES_DETECTED');
  }

  const changeEntries = Object.entries(changes).map(([field, [oldVal, newVal]]) => {
    const fieldName = formatFieldName(field);
    const oldValue = formatFieldValue(field, oldVal);
    const newValue = formatFieldValue(field, newVal);
    return `<div class="mb-1"><span class="font-medium">${fieldName}${t('TICKETS.COLON')}</span> <span class="line-through">${oldValue}</span> ${t('TICKETS.ARROW')} <span class="font-medium">${newValue}</span></div>`;
  });

  const updatedBy = meta.updated_by_user_name;
  let tooltipContent = changeEntries.join('');
  
  if (updatedBy) {
    tooltipContent += `<div class="mt-2 pt-2 border-t border-n-slate-6"><span class="font-medium">${t('TICKETS.SLA.UPDATED_BY')}${t('TICKETS.COLON')}</span> ${updatedBy}</div>`;
  }

  return tooltipContent;
};

const eventTooltip = computed(() => {
  const tooltipContent = getUpdatedEventTooltip(props.event);
  if (!tooltipContent) {
    return null;
  }
  return {
    content: tooltipContent,
    placement: 'top',
    html: true,
  };
});
</script>

<template>
  <!-- Compact -->
  <div
    v-if="props.compact"
    class="pl-2 rounded-lg transition-colors border-l-4 border-r-4 border-n-slate-11 flex flex-col justify-between"
  >
    <div v-if="showEventType" class="w-48 flex justify-end items-end">
      <div
        v-tooltip="eventTooltip"
        class="text-sm font-medium text-n-slate-11"
      >
        {{ getSlaEventTypeText(event.event_type) }}
      </div>
    </div>

    <div v-if="showDetails" class="w-48 pt-1">
      <div class="flex flex-col justify-end text-xs text-n-slate-11">
        <div class="">
          {{ t('TICKETS.SLA.FINISHED') }}{{ t('TICKETS.COLON')
          }}{{ formatDate(getEventDetails(event).finished) }}
        </div>
        <div class="">
          {{ t('TICKETS.SLA.DEADLINE') }}{{ t('TICKETS.COLON')
          }}{{ formatDate(getEventDetails(event).deadline) }}
        </div>
      </div>
    </div>

    <div v-if="showIcon" class="w-24 pt-1">
      <div
        class="flex px-2 py-2 text-sm justify-center font-medium rounded-full w-full"
        :class="getEventClass(event).class"
      >
        <div class="items-center">{{ getEventClass(event).text }}</div>
      </div>
    </div>
  </div>

  <!-- Full -->
  <div
    v-else
    class="p-3 rounded-lg transition-colors border-l-4 border-n-slate-11 flex flex-row justify-between"
  >
    <div v-if="showTicketName" class="w-48">
      <div class="flex flex-col justify-start items-start">
        <div class="text-sm font-medium text-n-slate-12">
          {{ event.ticket_title }}
        </div>
        <div class="text-xs text-n-slate-11">
          {{ t('TICKETS.SLA.TICKET_NUMBER', { number: event.ticket_id }) }}
        </div>
      </div>
    </div>

    <div v-tooltip="eventTooltip">
      <div v-if="showEventType" class="w-48 flex flex-col justify-center items-center">
        <div class="text-sm font-medium text-n-slate-12">
          {{ event.meta?.sla_policy_name }}
        </div>  
        <div class="text-xs font-medium text-n-slate-11">
          {{ getSlaEventTypeText(event.event_type) }}
        </div>
      </div>
    </div>

    <div v-if="showDetails" class="w-56 p-1">
      <div class="flex flex-col justify-end items-end text-xs text-n-slate-11">
        <div class="">
          {{ t('TICKETS.SLA.FINISHED') }}{{ t('TICKETS.COLON')
          }}{{ formatDate(getEventDetails(event).finished) }}
        </div>
        <div class="">
          {{ t('TICKETS.SLA.DEADLINE') }}{{ t('TICKETS.COLON')
          }}{{ formatDate(getEventDetails(event).deadline) }}
        </div>
      </div>
    </div>

    <div v-if="showIcon" class="w-24 p-1">
      <div
        class="flex px-2 py-2 text-sm justify-center font-medium rounded-full w-full"
        :class="getEventClass(event).class"
      >
        <div class="items-center">{{ getEventClass(event).text }}</div>
      </div>
    </div>
  </div>
</template>
